import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { Button, PageTitle } from "../StyledElements";
import { Dialog } from "../Dialog";
import { deleteClientApiKey, saveClientApiKey } from "../../services/clientApiKeys";
import { useClientApiKeys } from "../../hooks/useClientApiKeys";
import { resolveClientPublicId } from "../../utils/clientApiKeys";
import { useAuthContext } from "../../contexts/AuthContext";
import { isoToLocalInput, localInputToIso } from "../../utils/datetime";

// Manage the per-client REST API keys nodes can reference by name.

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  color: var(--main-font-color);
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Card = styled.div`
  border: 1px solid var(--content-item-border-color);
  background-color: var(--card-bg);
  border-radius: 8px;
  padding: 16px;
  box-shadow: var(--content-item-box-shadow);
`;

const Muted = styled.div`
  font-size: 13px;
  font-style: italic;
  color: #a0a6b1;
`;

const ErrorBanner = styled.div`
  padding: 10px 12px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #c0392b;
  background-color: rgba(192, 57, 43, 0.1);
  color: #c0392b;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 1fr 1.6fr 1fr 1.2fr auto;
  gap: 10px;
  align-items: center;
  padding: 10px 8px;
  border-bottom: 1px solid var(--content-item-border-color);
  font-size: 13px;

  &:last-child {
    border-bottom: none;
  }
`;

const HeadRow = styled(Row)`
  font-weight: 700;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #a0a6b1;
`;

const Mono = styled.span`
  font-family: monospace;
  font-size: 12px;
  word-break: break-all;
`;

const Small = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 12px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--main-font-color);
`;

const inputStyles = `
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;
`;

const Input = styled.input`
  ${inputStyles}
`;

const Select = styled.select`
  ${inputStyles}
  cursor: pointer;
`;

const Hint = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #a0a6b1;
`;

const emptyForm = {
  name: "",
  secret: "",
  refreshToken: "",
  service: "",
  description: "",
  accessTokenExpiresAt: "",
  refreshTokenExpiresAt: "",
};

/**
 * @param {object} props
 * @param {"client"|"admin"} [props.scope]
 * @param {string|null} [props.clientId] Scope for the client console.
 * @param {object[]} [props.clients] Client list for the admin console.
 */
export const ClientApiKeysManager = ({ scope = "client", clientId = null, clients = [] }) => {
  const { activeUser } = useAuthContext();
  const isClientUser = Boolean(String(activeUser?.user_type ?? "").match(/client/i));

  const isAdminScope = scope === "admin";

  const clientOptions = useMemo(
    () =>
      // Always key on the client's public id — the numeric id is not accepted by the API.
      (clients ?? [])
        .filter((client) => client?.public_id)
        .map((client) => ({
          value: client.public_id,
          label: client.name ?? client.public_id,
        })),
    [clients]
  );

  const [activeClientId, setActiveClientId] = useState(clientId ?? clientOptions[0]?.value ?? null);

  useEffect(() => {
    if (!isAdminScope) {
      setActiveClientId(clientId ?? null);
      return;
    }
    if (!activeClientId && clientOptions.length > 0) {
      setActiveClientId(clientOptions[0].value);
    }
  }, [isAdminScope, clientId, clientOptions, activeClientId]);

  // The API keys endpoints are scoped by the client's public id. Resolve one before asking:
  // an unscoped request just comes back "client_id is required".
  const scopedClientId =
    (isAdminScope ? activeClientId : clientId) ??
    resolveClientPublicId({ activeUser, clients });

  // On a client screen the server scopes to the active client, so query even when the
  // public id has not resolved locally. The admin console must name its client explicitly.
  const canQuery = isAdminScope ? Boolean(scopedClientId) : true;

  const { keys, loading, error, reload, clientId: listClientId } = useClientApiKeys({
    clientId: scopedClientId,
    enabled: canQuery,
  });

  // Prefer the scoped id, then whatever the list response told us.
  const effectiveClientId = scopedClientId ?? listClientId ?? null;

  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [actionNotice, setActionNotice] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const openCreate = () => {
    setForm(emptyForm);
    setFormError(null);
    setShowDialog(true);
  };

  const submit = async () => {
    if (saving) return;

    const name = form.name.trim();
    if (!name) {
      setFormError("A name is required — nodes reference the key by this name.");
      return;
    }
    if (!form.secret.trim()) {
      setFormError("The secret (access token) is required.");
      return;
    }

    // Admin users must pick a client; on a client screen the server scopes to the active client,
    // so an unresolved public id is not a blocker there.
    if (isAdminScope && !scopedClientId) {
      setFormError("Choose the client this key belongs to.");
      return;
    }

    setSaving(true);
    setFormError(null);

    const result = await saveClientApiKey({
      // Client-admins are scoped server-side, so they must not send a client id.
      clientId: isClientUser ? null : scopedClientId ?? null,
      name,
      secret: form.secret,
      refreshToken: form.refreshToken,
      service: form.service.trim(),
      description: form.description.trim(),
      accessTokenExpiresAt: form.accessTokenExpiresAt,
      refreshTokenExpiresAt: form.refreshTokenExpiresAt,
    });

    setSaving(false);

    if (result?.success === false) {
      setFormError(result.message || "Could not save the key.");
      return;
    }

    setShowDialog(false);
    reload();
    // Node pickers listening for key changes re-fetch their options.
    window.dispatchEvent(new CustomEvent("client-api-keys:changed"));
    setActionNotice(`"${name}" is stored.`);
  };

  const remove = async (key) => {
    // The row already asked for confirmation.
    setActionError(null);
    setActionNotice(null);
    setConfirmDelete(null);

    const result = await deleteClientApiKey(key.name, { clientId: effectiveClientId });

    if (result?.success === false) {
      setActionError(
        `${result.message || "Could not delete the key."}${result.status ? ` (HTTP ${result.status})` : ""}`
      );
      return;
    }

    reload();
    window.dispatchEvent(new CustomEvent("client-api-keys:changed"));
  };

  return (
    <Wrapper>
      <Header>
        <PageTitle title="Client API Keys" style={{ marginBottom: 0 }} />
        <div style={{ flex: 1 }} />

        {isAdminScope ? (
          <Select
            value={scopedClientId ?? ""}
            onChange={(event) => setActiveClientId(event.target.value)}
            style={{ width: 260 }}
            aria-label="Client"
          >
            {clientOptions.length === 0 ? <option value="">No clients</option> : null}
            {clientOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </Select>
        ) : null}

        <Small onClick={reload} title="Reload">
          <Icon icon="mdi:refresh" /> Reload
        </Small>
        <Button onClick={openCreate}>+ Add key</Button>
      </Header>

      <Muted>
        Bound to the client by its public id and stored encrypted. Node auth references a key by
        name; secrets and refresh keys are never shown again — only a masked value.
      </Muted>

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      {actionError ? <ErrorBanner>{actionError}</ErrorBanner> : null}
      {actionNotice ? (
        <div
          style={{
            padding: "10px 12px",
            fontSize: "14px",
            borderRadius: "6px",
            border: "1px solid #2ecc71",
            backgroundColor: "rgba(46, 204, 113, 0.1)",
            color: "#2ecc71",
          }}
        >
          {actionNotice}
        </div>
      ) : null}

      {!canQuery ? (
        <Muted>Select a client to manage its API keys.</Muted>
      ) : null}

      <Card>
        {loading ? (
          <Muted>Loading keys…</Muted>
        ) : keys.length === 0 ? (
          <Muted>No keys yet. Add one to let nodes authenticate against an external service.</Muted>
        ) : (
          <>
            <HeadRow>
              <span>Name</span>
              <span>Service</span>
              <span>Description</span>
              <span>Value</span>
              <span>Updated</span>
              <span />
            </HeadRow>
            {keys.map((key) => (
              <Row key={key.name}>
                <Mono>{key.name}</Mono>
                <span>{key.service ?? <Muted>—</Muted>}</span>
                <span>{key.description ?? <Muted>—</Muted>}</span>
                <Mono>{key.maskedValue ?? <Muted>hidden</Muted>}</Mono>
                <span>{key.updatedAt ? String(key.updatedAt) : <Muted>—</Muted>}</span>
                {confirmDelete === key.name ? (
                  <div style={{ display: "flex", gap: "4px" }}>
                    <Small onClick={() => remove(key)} title="Delete this key permanently">
                      <Icon icon="mdi:trash-can-outline" /> Confirm
                    </Small>
                    <Small onClick={() => setConfirmDelete(null)}>
                      <Icon icon="mdi:close" /> Cancel
                    </Small>
                  </div>
                ) : (
                  <Small onClick={() => setConfirmDelete(key.name)} title="Delete this key">
                    <Icon icon="mdi:trash-can-outline" /> Delete
                  </Small>
                )}
              </Row>
            ))}
          </>
        )}
      </Card>

      <Dialog
        title="Add a client API key"
        subtitle="Saving an existing name replaces its secret and refresh token"
        show={showDialog}
        onClose={() => setShowDialog(false)}
        onOK={submit}
        okText={saving ? "Saving…" : "Save"}
        closeText="Cancel"
        style={{ width: "90%", maxWidth: "560px" }}
      >
        <Field>
          <Label>Name</Label>
          <Input
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            placeholder="weather"
            autoFocus
          />
          <Hint>Nodes reference this name (e.g. auth.clientKey = "weather").</Hint>
        </Field>

        <Field>
          <Label>Secret (access token)</Label>
          <Input
            type="password"
            value={form.secret}
            onChange={(event) => setForm((current) => ({ ...current, secret: event.target.value }))}
            placeholder="xoxb-…"
            autoComplete="new-password"
          />
          <Hint>Write-only: it is encrypted at rest and never returned.</Hint>
        </Field>

        <Field>
          <Label>Refresh token</Label>
          <Input
            type="password"
            value={form.refreshToken}
            onChange={(event) => setForm((current) => ({ ...current, refreshToken: event.target.value }))}
            placeholder="optional refresh token"
            autoComplete="new-password"
          />
          <Hint>Only for services that issue one. Also encrypted and write-only.</Hint>
        </Field>

        <Field>
          <Label>Access token expires at</Label>
          <Input
            type="datetime-local"
            value={isoToLocalInput(form.accessTokenExpiresAt)}
            onChange={(event) =>
              setForm((current) => ({ ...current, accessTokenExpiresAt: localInputToIso(event.target.value) }))
            }
          />
          <Hint>Optional. Sent as ISO-8601 UTC.</Hint>
        </Field>

        <Field>
          <Label>Refresh token expires at</Label>
          <Input
            type="datetime-local"
            value={isoToLocalInput(form.refreshTokenExpiresAt)}
            onChange={(event) =>
              setForm((current) => ({ ...current, refreshTokenExpiresAt: localInputToIso(event.target.value) }))
            }
          />
          <Hint>Optional. Sent as ISO-8601 UTC.</Hint>
        </Field>

        <Field>
          <Label>Service</Label>
          <Input
            value={form.service}
            onChange={(event) => setForm((current) => ({ ...current, service: event.target.value }))}
            placeholder="open-meteo"
          />
        </Field>

        <Field>
          <Label>Description</Label>
          <Input
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
            placeholder="Weather API"
          />
        </Field>

        {formError ? <ErrorBanner>{formError}</ErrorBanner> : null}
      </Dialog>
    </Wrapper>
  );
};

export default ClientApiKeysManager;
